import React from 'react';

import { Checkbox, Skeleton, Stack, Table } from '@mantine/core';
import AthleteStatusBadge from 'components/status-badges/AthleteStatusBadge';

export interface AthleteCandidate {
  athleteId: string;
  fullName: string;
  athleteStatus: string | null;
}

interface IAthleteCandidateTableProps {
  candidates: AthleteCandidate[];
  selectedIds: Set<string>;
  onToggle: (athleteId: string) => void;
  onToggleAll?: () => void;
  isLoading: boolean;
  disabled?: boolean;
  checkboxDataCy: string;
}

const AthleteCandidateTable = ({
  candidates,
  selectedIds,
  onToggle,
  onToggleAll,
  isLoading,
  disabled = false,
  checkboxDataCy,
}: IAthleteCandidateTableProps): JSX.Element => {
  const selectedCount = candidates.filter(c => selectedIds.has(c.athleteId)).length;

  return isLoading ? (
    <Stack spacing='xs'>
      <Skeleton height={36} />
      <Skeleton height={36} />
      <Skeleton height={36} />
    </Stack>
  ) : (
    <div style={{ maxHeight: 320, overflowY: 'auto' }}>
      <Table verticalSpacing='sm' highlightOnHover>
        <thead>
          <tr>
            <th style={{ width: '60%' }}>
              {onToggleAll ? (
                <Checkbox
                  checked={candidates.length > 0 && selectedCount === candidates.length}
                  indeterminate={selectedCount > 0 && selectedCount < candidates.length}
                  onChange={onToggleAll}
                  disabled={disabled || candidates.length === 0}
                  label='Select all'
                  data-cy={`${checkboxDataCy}SelectAll`}
                />
              ) : (
                'Athlete'
              )}
            </th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map(candidate => (
            <tr key={candidate.athleteId}>
              <td>
                <Checkbox
                  checked={selectedIds.has(candidate.athleteId)}
                  onChange={() => onToggle(candidate.athleteId)}
                  disabled={disabled}
                  label={candidate.fullName}
                  data-cy={checkboxDataCy}
                />
              </td>
              <td>{candidate.athleteStatus && <AthleteStatusBadge status={candidate.athleteStatus} />}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default AthleteCandidateTable;
